<template>
  <!-- IP Infos -->
  <div class="ip-data-section mb-4 mt-4">
    <div class="jn-title2">
      <h2 id="IPInfo" class="col-4" :class="{ 'mobile-h2': isMobile }">🔎 {{ t('ipInfos.Title') }}</h2>
    </div>
    <div class="text-secondary">
      <p>{{ t('ipInfos.Notes') }}</p>
    </div>
    
    <!-- IP Search Box -->
    <div class="ip-search-section mb-4">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-6">
          <div class="input-group">
            <input 
              type="text" 
              class="form-control" 
              :placeholder="t('ipInfos.searchPlaceholder', 'Enter IP address to lookup')"
              v-model="searchIP"
              @keyup.enter="searchIPAddress"
              :class="{ 'is-invalid': searchError }"
            >
            <button 
              class="btn btn-primary" 
              type="button" 
              @click="searchIPAddress"
              :disabled="isSearching"
            >
              <span v-if="isSearching" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ isSearching ? t('ipInfos.searching', 'Searching...') : t('ipInfos.search', 'Search') }}
            </button>
          </div>
          <div v-if="searchError" class="invalid-feedback d-block">
            {{ searchError }}
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div v-for="(card, index) in ipDataCards.slice(0, ipCardsToShow)" :key="card.id" :ref="card.id" :class="[colClass, {
        'jn-opacity': !card.ip || card.ip === t('ipInfos.IPv4Error') || card.ip === t('ipInfos.IPv6Error')
      }]">
        <IPCard :card="card" :index="index" :isDarkMode="isDarkMode" :isMobile="isMobile" :ipGeoSource="ipGeoSource"
          :isMapShown="isMapShown" :isCardsCollapsed="isCardsCollapsed" :copiedStatus="copiedStatus" :configs="configs"
          :asnInfos="asnInfos" @refresh-card="refreshCard" />
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, reactive, watch } from 'vue';
import { useMainStore } from '@/store';
import { useI18n } from 'vue-i18n';
import { trackEvent } from '@/utils/use-analytics';
import { isValidIP } from '@/utils/valid-ip.js';
import { transformDataFromIPapi, transformQualityAnalysisData } from '@/utils/transform-ip-data.js';
import { getIPFromIPIP, getIPFromCloudflare_V4, getIPFromCloudflare_V6, getIPFromIPinfo, getIPFromIPAPI_V4, getIPFromIPAPIIS_V6, getIPFromIPREGISTRY } from '@/utils/getips';
import { authenticatedFetch } from '@/utils/authenticated-fetch';
import { fetchQualityAnalysis } from '@/utils/quality-analysis';
import { formatConsensusData } from '@/utils/quality-analysis';
import IPCard from './ip-infos/IPCard.vue';


const { t } = useI18n();

// Store
const store = useMainStore();
const isDarkMode = computed(() => store.isDarkMode);
const isMobile = computed(() => store.isMobile);
const configs = computed(() => store.configs);
const userPreferences = computed(() => store.userPreferences);
const lang = computed(() => store.lang);

// 页面的动态配置
const isMapShown = computed(() => userPreferences.value.showMap);
const isCardsCollapsed = computed(() => userPreferences.value.simpleMode);


// 创建样式
const colClass = computed(() => {
  const numCards = ipCardsToShow.value;
  if (numCards > 0) {
    // 保证每行不超过三个卡片
    const colSize = numCards > 3 ? 4 : Math.floor(12 / numCards);
    return `col-xl-${colSize} col-md-${colSize}  mb-4`;
  }
  return 'col-xl-4 col-lg-6 col-md-6 mb-4'; // 默认情况
});

// 默认卡片数据
const createDefaultCard = () => ({
  ip: "",
  country_name: "",
  region: "",
  city: "",
  latitude: "",
  longitude: "",
  isp: "",
  asn: "",
  asnlink: "",
  mapUrl: '/res/defaultMap.webp',
  mapUrl_dark: '/res/defaultMap_dark.webp',
});

// IP 数据卡片
const ipDataCards = reactive([
  {
    ...createDefaultCard(),
    id: "cnsource",
    source: "IPIP.net",
  },
  {
    ...createDefaultCard(),
    id: "cloudflare_v4",
    source: "Cloudflare IPv4",
  },
  {
    ...createDefaultCard(),
    id: "cloudflare_v6",
    source: "Cloudflare IPv6",
  },
  {
    ...createDefaultCard(),
    id: "ipinfo_dual",
    source: "IPinfo.io",
  },
  {
    ...createDefaultCard(),
    id: "ipapi_v4",
    source: "IPREGISTRY IPv4",
  },
  {
    ...createDefaultCard(),
    id: "ipapiis_v6",
    source: "IPAPI.is IPv6",
  },
]);

// 默认 ASN 信息
const asnInfos = ref({
  "AS888888": {
    "asnName": "Google", "asnOrgName": "GOGL-ARIN", "estimatedUsers": "888888", "IPv4_Pct": "95.35", "IPv6_Pct": "4.65", "HTTP_Pct": "3.16", "HTTPS_Pct": "96.84", "Desktop_Pct": "58.88", "Mobile_Pct": "41.12", "Bot_Pct": "98.46", "Human_Pct": "1.54"
  }
});

// 其它数据
const ipCardsToShow = ref(userPreferences.value.ipCardsToShow);
const copiedStatus = ref({});
const IPArray = ref([]);
const ipGeoSource = ref(userPreferences.value.ipGeoSource);
const usingSource = ref(userPreferences.value.ipGeoSource);
const fetchStatus = reactive([]);

// 搜索功能相关变量
const searchIP = ref('');
const isSearching = ref(false);
const searchError = ref('');

// 中间件
let pendingIPDetailsRequests = new Map();
let ipDataCache = new Map();

// 公共获取 IP 地址方法
const fetchIP = async (cardID, getFromSource) => {
  console.log(`[DEBUG] fetchIP called for cardID: ${cardID}`);
  console.log(`[DEBUG] getFromSource function:`, getFromSource.name);
  console.log(`[DEBUG] configs.value.originalSite:`, configs.value.originalSite);
  
  const { ip, source } = await getFromSource(configs.value.originalSite);
  console.log(`[DEBUG] Got IP result for cardID ${cardID}:`, { ip, source });
  
  let fetchingStatus = false;
  if (ip !== null) {
    console.log(`[DEBUG] Setting IP ${ip} for card ${cardID}`);
    ipDataCards[cardID].ip = ip;
    ipDataCards[cardID].source = source;
    IPArray.value = [...IPArray.value, ip];
    
    // Use different IP geo source for each card to get diverse data
    const cardSourceMapping = {
      0: 0, // CN Source -> myip.cloudmini.net
      1: 1, // Cloudflare IPv4 -> IPinfo.io
      2: 2, // Cloudflare IPv6 -> IP-API.com
      3: 3, // IPinfo.io -> IPAPI.is
      4: 9, // IPREGISTRY IPv4 -> IPRegistry
      5: 7, // IPAPI.is IPv6 -> IPGeolocation.io (new service)
    };
    
    const sourceId = cardSourceMapping[cardID] !== undefined ? cardSourceMapping[cardID] : cardID;
    console.log(`[DEBUG] Fetching IP details for card ${cardID} with sourceId: ${sourceId}`);
    await fetchIPDetails(cardID, ip, sourceId);
  } else if (cardID === 2 || cardID === 5) {
    console.log(`[DEBUG] Setting IPv6 error for card ${cardID}`);
    ipDataCards[cardID].ip = t('ipInfos.IPv6Error');
  } else {
    console.log(`[DEBUG] Setting IPv4 error for card ${cardID}`);
    ipDataCards[cardID].ip = t('ipInfos.IPv4Error');
  }
  // 总是返回 true，即使获取 IP 失败，以便在 trackFetchStatus 中记录
  fetchingStatus = true;
  fetchStatus[cardID] = { [cardID]: fetchingStatus };
  trackFetchStatus(fetchStatus);
  console.log(`[DEBUG] Completed fetchIP for cardID: ${cardID}`);
};

// 上报数据获取状态，并发送到 store
const trackFetchStatus = (status) => {
  let allHasFetched = true;
  for (let i = 0; i < ipCardsToShow.value; i++) {
    if (status[i] === undefined) {
      allHasFetched = false;
    } else {
      allHasFetched = allHasFetched && status[i][i];
    }
  }
  if (allHasFetched) {
    store.setLoadingStatus('ipcheck', true);
  }
};

// 检查所有 IP 地址
const checkAllIPs = async () => {
  const ipFunctions = [
    () => fetchIP(0, getIPFromIPIP),
    () => fetchIP(1, getIPFromCloudflare_V4),
    () => fetchIP(2, getIPFromCloudflare_V6),
    () => fetchIP(3, getIPFromIPinfo),
    () => fetchIP(4, getIPFromIPREGISTRY),
    () => fetchIP(5, getIPFromIPAPIIS_V6),
  ];

  // 限制执行的函数数量为 ipCardsToShow 的长度
  const maxIndex = ipCardsToShow.value;

  let index = 0;
  const interval = setInterval(() => {
    if (index < maxIndex && index < ipFunctions.length) {
      ipFunctions[index].call(this);
      index++;
    } else {
      clearInterval(interval);
    }
  }, 500);
};

// 从 IP 地址获取 IP 详细信息
const fetchIPDetails = async (cardIndex, ip, sourceID = null) => {
  console.log(`[DEBUG] fetchIPDetails called for cardIndex: ${cardIndex}, ip: ${ip}, sourceID: ${sourceID}`);
  sourceID = sourceID || ipGeoSource.value;
  const card = ipDataCards[cardIndex];
  card.ip = ip;
  let setLang = lang.value;
  if (setLang === 'zh') {
    setLang = 'zh-CN';
  }

  // Create cache key that includes both IP and source ID to allow different sources for same IP
  const cacheKey = `${ip}-${sourceID}`;
  console.log(`[DEBUG] Cache key: ${cacheKey}`);
  
  // 检查缓存中是否已有该 IP 和源的数据
  if (ipDataCache.has(cacheKey)) {
    console.log(`[DEBUG] Using cached data for: ${cacheKey}`);
    const cachedData = ipDataCache.get(cacheKey);
    Object.assign(card, cachedData);
    return;
  }

  // 检查是否有正在进行的查询，如果有，则等待该查询完成
  if (pendingIPDetailsRequests.has(cacheKey)) {
    console.log(`[DEBUG] Waiting for pending request: ${cacheKey}`);
    await pendingIPDetailsRequests.get(cacheKey);
    const cachedData = ipDataCache.get(cacheKey);
    if (cachedData) {
      Object.assign(card, cachedData);
    }
    return;
  }

  const fetchPromise = (async () => {
    console.log(`[DEBUG] Starting fetchPromise for cardIndex: ${cardIndex}`);
    const sources = store.ipDBs.filter(source => source.enabled);
    console.log(`[DEBUG] Available sources:`, sources);

    let currentSourceIndex = sourceID !== null ? sources.findIndex(source => source.id === sourceID) : 0;
    let attempts = 0;
    console.log(`[DEBUG] Starting with sourceIndex: ${currentSourceIndex}, sourceID: ${sourceID}`);

    while (attempts < sources.length) {
      const source = sources[currentSourceIndex];
      console.log(`[DEBUG] Trying source:`, source);
      try {
        const url = store.getDbUrl(source.id, ip, setLang);
        console.log(`[DEBUG] Fetching from URL: ${url}`);
        const response = await authenticatedFetch(url);
        console.log(`[DEBUG] Response received:`, response);
        const cardData = transformDataFromIPapi(response, source.id, t, lang.value);
        console.log(`[DEBUG] Transformed card data:`, cardData);

        if (cardData) {
          console.log(`[DEBUG] Card data is valid, updating card`);
          ipGeoSource.value = source.id;
          usingSource.value = source.id;
          store.updatePreference('ipGeoSource', source.id);
          Object.assign(card, cardData);
          console.log(`[DEBUG] Card updated:`, card);
          
                          // Fetch quality analysis for all cards with valid IPs
                          if (isValidIP(ip)) {
                            try {
                              const qualityData = await fetchQualityAnalysis(ip);
                              
                              if (qualityData && qualityData.analysis) {
                                const transformedQuality = transformQualityAnalysisData(qualityData, t);
                                transformedQuality.consensus = formatConsensusData(qualityData.provider_consensus || {});
                                card.qualityAnalysis = transformedQuality;
                              }
                            } catch (error) {
                              // Continue without quality analysis if it fails
                              console.error('Error fetching quality analysis for IP', ip + ':', error.message);
                            }
                          }
          
          // Cache the card data including quality analysis
          const dataToCache = { ...cardData };
          if (card.qualityAnalysis) {
            dataToCache.qualityAnalysis = card.qualityAnalysis;
          }
          ipDataCache.set(cacheKey, dataToCache);
          return;
        }
      } catch (error) {
        console.error("Error fetching IP details from source " + source.id + ":", error);
        store.updateIPDBs({ id: source.id, enabled: false });
        currentSourceIndex = (currentSourceIndex + 1) % sources.length;
        attempts++;
      }
    }

    throw new Error("All sources failed to fetch IP details for IP: " + ip);
  })();

  // 将此 Promise 存储在 pendingIPDetailsRequests 中，以避免重复查询
  pendingIPDetailsRequests.set(cacheKey, fetchPromise);

  try {
    await fetchPromise;
  } catch (error) {
    console.error(error);
    throw error;
  } finally {
    // 完成后，从 pendingIPDetailsRequests 中移除
    pendingIPDetailsRequests.delete(cacheKey);
  }
};

// 在重新选择 IP 数据库源时，更新 IP 地理数据
const selectIPGeoSource = () => {
  // 清空部分数据
  ipDataCards.forEach((card) => {
    const { ip, mapUrl, mapUrl_dark } = card;
    Object.assign(card, createDefaultCard(), { ip, mapUrl, mapUrl_dark });
  });

  ipDataCache.clear();

  // 尝试更新一次，成功后再获取其他 IP 数据
  let runningSource = fetchIPDetails(0, ipDataCards[0].ip, ipGeoSource.value);

  // 重新获取 IP 数据
  let index = 1;
  const interval = setInterval(() => {
    if (index < ipDataCards.length) {
      const card = ipDataCards[index];
      if (isValidIP(card.ip)) {
        fetchIPDetails(index, card.ip, parseInt(runningSource));
      }
      index++;
    } else {
      clearInterval(interval);
    }
  }, 500);
};

// 刷新某张卡片
const refreshCard = (card, index) => {
  console.log(`[DEBUG] refreshCard called for index: ${index}`);
  console.log(`[DEBUG] Card before clearing:`, card);
  clearCardData(card);
  console.log(`[DEBUG] Card after clearing:`, card);
  
  switch (index) {
    case 0:
      console.log('[DEBUG] Refreshing card 0 with getIPFromIPIP');
      fetchIP(0, getIPFromIPIP);
      break;
    case 1:
      console.log('[DEBUG] Refreshing card 1 with getIPFromCloudflare_V4');
      fetchIP(1, getIPFromCloudflare_V4);
      break;
    case 2:
      console.log('[DEBUG] Refreshing card 2 with getIPFromCloudflare_V6');
      fetchIP(2, getIPFromCloudflare_V6);
      break;
    case 3:
      console.log('[DEBUG] Refreshing card 3 with getIPFromIPinfo');
      fetchIP(3, getIPFromIPinfo);
      break;
    case 4:
      console.log('[DEBUG] Refreshing card 4 with getIPFromIPREGISTRY');
      fetchIP(4, getIPFromIPREGISTRY);
      break;
    case 5:
      console.log('[DEBUG] Refreshing card 5 with getIPFromIPAPIIS_V6');
      fetchIP(5, getIPFromIPAPIIS_V6);
      break;
    default:
      console.error("Undefind Source:");
  }
  trackEvent('MyIP', 'RefreshClick', 'IPInfos');
};

// 清空卡片数据
const clearCardData = (card) => {
  Object.assign(card, createDefaultCard());
};

// 获取特定卡片的 IP 详细信息（按卡片索引）
const fetchIPDetailsForCard = async (cardIndex, ip) => {
  const card = ipDataCards[cardIndex];
  
  try {
    // Use different IP geo source for each card to get diverse data
    const cardSourceMapping = {
      0: 0, // CN Source -> myip.cloudmini.net
      1: 1, // Cloudflare IPv4 -> IPinfo.io
      2: 2, // Cloudflare IPv6 -> IP-API.com
      3: 3, // IPinfo.io -> IPAPI.is
      4: 9, // IPREGISTRY IPv4 -> IPRegistry
      5: 7, // IPAPI.is IPv6 -> IPGeolocation.io (new service)
    };
    
    const sourceId = cardSourceMapping[cardIndex] !== undefined ? cardSourceMapping[cardIndex] : cardIndex;
    await fetchIPDetails(cardIndex, ip, sourceId);
    
  } catch (error) {
    console.error(`Error fetching IP details for card ${cardIndex}:`, error);
    // On error, keep the IP but clear other fields
    Object.assign(card, createDefaultCard());
    card.ip = ip;
    card.source = ipDataCards[cardIndex].source; // Keep original source name
  }
};

// 搜索 IP 地址功能
const searchIPAddress = async () => {
  const ip = searchIP.value.trim();
  
  // 清除之前的错误
  searchError.value = '';
  
  // 验证 IP 地址
  if (!ip) {
    searchError.value = t('ipInfos.emptyIP', 'Please enter an IP address');
    return;
  }
  
  if (!isValidIP(ip)) {
    searchError.value = t('ipInfos.invalidIP', 'Please enter a valid IP address');
    return;
  }
  
  isSearching.value = true;
  
  try {
    // 为所有卡片更新 IP 信息
    const promises = [];
    for (let i = 0; i < ipCardsToShow.value; i++) {
      const card = ipDataCards[i];
      // 清空当前数据
      Object.assign(card, createDefaultCard());
      card.ip = ip;
      // 保持原有源标识
      const originalSource = ipDataCards[i].source;
      card.source = originalSource;
      
      // 获取 IP 详细信息（使用各自的数据源）
      promises.push(fetchIPDetailsForCard(i, ip));
    }
    
    // 等待所有请求完成
    await Promise.allSettled(promises);
    
    // 跟踪事件
    trackEvent('MyIP', 'SearchIP', 'IPInfos');
    
  } catch (error) {
    console.error('Error searching IP:', error);
    searchError.value = t('ipInfos.searchError', 'Failed to fetch IP information');
  } finally {
    isSearching.value = false;
  }
};

watch(() => userPreferences.value.ipGeoSource, (newVal, oldVal) => {
  ipGeoSource.value = newVal;
  if (newVal !== usingSource.value) {
    selectIPGeoSource();
  }
});


watch(IPArray, () => {
  store.updateAllIPs(IPArray.value);
});

onMounted(() => {
  store.setMountingStatus('ipcheck', true);
});

defineExpose({
  checkAllIPs,
  ipDataCards,
  refreshCard,
});

</script>

<style scoped>
</style>